/**
 * Logger utility for application-wide logging
 * 
 * Provides structured logging with different log levels, automatic sanitization
 * of sensitive data, and environment-aware behavior (console in dev, structured
 * logging in production).
 * 
 * @example
 * ```ts
 * import { logger } from '@/utils/logger'
 * 
 * logger.info('User logged in', { userId: '123' })
 * logger.error('API request failed', { endpoint: '/api/users', status: 500 })
 * logger.warn('Deprecated function called', { function: 'oldMethod' })
 * ```
 */

type LogLevel = 'error' | 'warn' | 'info' | 'debug'

/**
 * Log entry structure
 */
interface LogEntry {
  level: LogLevel
  message: string
  meta?: Record<string, any>
  timestamp: string
  environment: string
  userId?: string
  sessionId?: string
}

/**
 * Logger class for application-wide logging
 * 
 * Features:
 * - Automatic sanitization of sensitive fields (password, token, etc.)
 * - Session tracking for client-side logs
 * - Environment-aware (console in dev, structured in prod)
 * - Always logs errors to console for debugging
 */
class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development'
  private isClient = typeof window !== 'undefined'

  private createLogEntry(level: LogLevel, message: string, meta?: Record<string, any>): LogEntry {
    return {
      level,
      message,
      meta: this.sanitizeMeta(meta),
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV || 'unknown',
      sessionId: this.getSessionId(),
    }
  }

  private sanitizeMeta(meta?: Record<string, any>): Record<string, any> | undefined {
    if (!meta) return undefined

    // Remove sensitive fields
    const sensitiveFields = ['password', 'token', 'key', 'secret', 'auth', 'webhook']
    const sanitized = { ...meta }

    sensitiveFields.forEach((field) => {
      Object.keys(sanitized).forEach((key) => {
        if (key.toLowerCase().includes(field)) {
          sanitized[key] = '[REDACTED]'
        }
      })
    })

    return sanitized
  }

  private getSessionId(): string {
    if (!this.isClient) return 'server'

    try {
      // Generate or retrieve session ID
      let sessionId = sessionStorage.getItem('log-session-id')
      if (!sessionId) {
        sessionId = Math.random().toString(36).substring(2, 15)
        sessionStorage.setItem('log-session-id', sessionId)
      }
      return sessionId
    } catch {
      // Fallback if sessionStorage is not available
      return 'unknown'
    }
  }

  private log(level: LogLevel, message: string, meta?: Record<string, any>) {
    const entry = this.createLogEntry(level, message, meta)

    // Development logging - always show in console with nice formatting
    if (this.isDevelopment) {
      const consoleMethod =
        level === 'error'
          ? console.error
          : level === 'warn'
            ? console.warn
            : level === 'info'
              ? console.info
              : console.log

      const timestamp = new Date().toLocaleTimeString()
      const metaStr = entry.meta ? JSON.stringify(entry.meta, null, 2) : ''

      consoleMethod(`🔍 [${timestamp}] [${entry.level.toUpperCase()}] ${entry.message}`, metaStr ? '\n' + metaStr : '')
    }

    // Production logging - send to external service
    if (!this.isDevelopment) {
      this.sendToLoggingService(entry)
    }

    // Always log errors to console for debugging
    if (level === 'error') {
      console.error('[ERROR]', entry.message, entry.meta || '')
    }
  }

  private async sendToLoggingService(entry: LogEntry) {
    try {
      // Server-side logging only - structured console output for now
      if (!this.isClient) {
        // Production builds keep console.warn and strip console.log; removeConsole only sees direct calls, so no alias
        if (entry.level === 'warn') {
          console.warn(JSON.stringify(entry))
        } else {
          console.log(JSON.stringify(entry))
        }
      }
      // Note: Client-side logging has been simplified for this implementation
    } catch (error) {
      // Fallback to console if logging service fails
      console.error('Logging service failed:', error)
      console.log(entry)
    }
  }

  /**
   * Log an error message
   * 
   * @param message - The error message
   * @param meta - Optional metadata object (will be sanitized)
   * 
   * @example
   * ```ts
   * logger.error('Failed to fetch user', { userId: '123', endpoint: '/api/user' })
   * ```
   */
  error(message: string, meta?: Record<string, any>) {
    this.log('error' as LogLevel, message, meta)
  }

  /**
   * Log a warning message
   * 
   * @param message - The warning message
   * @param meta - Optional metadata object (will be sanitized)
   * 
   * @example
   * ```ts
   * logger.warn('Deprecated API endpoint used', { endpoint: '/api/v1/users' })
   * ```
   */
  warn(message: string, meta?: Record<string, any>) {
    this.log('warn' as LogLevel, message, meta)
  }

  /**
   * Log an info message
   * 
   * @param message - The info message
   * @param meta - Optional metadata object (will be sanitized)
   * 
   * @example
   * ```ts
   * logger.info('User action completed', { action: 'form_submit', formId: 'contact' })
   * ```
   */
  info(message: string, meta?: Record<string, any>) {
    this.log('info' as LogLevel, message, meta)
  }

  /**
   * Log a debug message (only in development)
   * 
   * @param message - The debug message
   * @param meta - Optional metadata object (will be sanitized)
   * 
   * @example
   * ```ts
   * logger.debug('Component rendered', { component: 'Hero', props: {...} })
   * ```
   */
  debug(message: string, meta?: Record<string, any>) {
    this.log('debug' as LogLevel, message, meta)
  }
}

/**
 * Singleton logger instance
 * Use this throughout the application for consistent logging
 */
export const logger = new Logger()

/**
 * Performance logging utility
 * 
 * Provides helpers for measuring and logging performance metrics
 * for async and sync operations.
 * 
 * @example
 * ```ts
 * import { performanceLogger } from '@/utils/logger'
 * 
 * // Measure async operation
 * const result = await performanceLogger.measureAsync('fetchUsers', async () => {
 *   return await fetch('/api/users')
 * })
 * 
 * // Measure sync operation
 * const processed = performanceLogger.measure('processData', () => {
 *   return heavyComputation(data)
 * })
 * ```
 */
export const performanceLogger = {
  /**
   * Measure and log the duration of an async operation
   * 
   * @param name - The name/identifier for this performance metric
   * @param fn - The async function to measure
   * @returns The result of the async function
   * @throws Re-throws any error from the function
   * 
   * @example
   * ```ts
   * const users = await performanceLogger.measureAsync('fetchUsers', async () => {
   *   return await api.getUsers()
   * })
   * // Logs: { metric: 'fetchUsers', duration: 234, success: true }
   * ```
   */
  measureAsync: async <T>(name: string, fn: () => Promise<T>): Promise<T> => {
    const start = Date.now()
    try {
      const result = await fn()
      logger.info('Performance metric', {
        metric: name,
        duration: Date.now() - start,
        success: true,
      })
      return result
    } catch (error) {
      logger.error('Performance metric failed', {
        metric: name,
        duration: Date.now() - start,
        error: error instanceof Error ? error.message : 'Unknown error',
      })
      throw error
    }
  },

  /**
   * Measure and log the duration of a synchronous operation
   * 
   * @param name - The name/identifier for this performance metric
   * @param fn - The synchronous function to measure
   * @returns The result of the function
   * @throws Re-throws any error from the function
   * 
   * @example
   * ```ts
   * const result = performanceLogger.measure('processData', () => {
   *   return expensiveComputation(data)
   * })
   * // Logs: { metric: 'processData', duration: 45, success: true }
   * ```
   */
  measure: <T>(name: string, fn: () => T): T => {
    const start = Date.now()
    try {
      const result = fn()
      logger.info('Performance metric', {
        metric: name,
        duration: Date.now() - start,
        success: true,
      })
      return result
    } catch (error) {
      logger.error('Performance metric failed', {
        metric: name,
        duration: Date.now() - start,
        error: error instanceof Error ? error.message : 'Unknown error',
      })
      throw error
    }
  },
}
